import React from 'react';
import { useSetAtom, useAtom, useAtomValue } from 'jotai';

import {
  AddressLookup,
  AddressManualEntryDialog,
  AddressManualEntryError,
  AddressManualEntryValues,
  DedicatedNmiSearchModal,
  constructDisplayLabel,
  getAddressFromOriginAddressId,
  getOriginAddresses,
  qasLookup,
  getOriginAddressesFromBatchId,
  QasProfile,
  type Address,
  type AddressResult,
  type ConnectionAddressObject,
  type IOriginAddressMeter,
  type AddressLookupProps,
} from '@origin-digital/address-lookup';

import {
  useNMIResolution,
  NMIResolution,
  NMIResolutionState,
} from '@origin-digital/nmi-resolution';

import { fromAddressToConnectionAddress } from './energy-address-mapper';
import { DEFAULT_POSTCODE } from './location-config';
import { useEnergyAddressConfig } from './EnergyAddressLookupProvider';

import {
  originAddressesAtom,
  addressEntryDedicatedNmiSearchDialogOpenAtom,
  addressEntryManualDialogTabAtom,
  addressConfirmDialogOpenAtom,
  selectedAddressAtom,
  selectedEnergyAtom,
  unconfirmedAddressAtom,
  addressLookupStatusAtom,
  addressManualDialogWithTabAtom,
} from './atoms';

import { CustomerType } from '@origin-digital/sales-common';
import { asyncResult } from './async-result';

import { useEnergyAddressLookup } from './useEnergyAddressLookup';
import { ORIGIN_ADDRESS_REVERSE_LOOKUP_API_URL, ORIGIN_ADDRESSES_API_URL } from './lookup-config';
import { createTraceHeaders } from './create-trace-headers';

export type EnergyAddressLookupProps = {
  userLocationPostcode?: string;
  onAddressSelected: (address: ConnectionAddressObject) => void;
  onAddressUnconfirmed?: (address: ConnectionAddressObject) => void;
  onAddressesUpdated?: (addresses: ConnectionAddressObject[]) => void;
  originAddressId?: string;
  label?: string;
  searchByNmi?: boolean;
  searchByMirn?: boolean;
  showDefaultPricing?: boolean;
  showEmptyAddressError?: boolean;
};

const EnergyAddressLookupRoot: React.FC<EnergyAddressLookupProps> = ({
  userLocationPostcode = DEFAULT_POSTCODE,
  onAddressSelected,
  onAddressUnconfirmed,
  onAddressesUpdated,
  originAddressId,
  label,
  searchByNmi = true,
  searchByMirn = true,
  showDefaultPricing = true,
  showEmptyAddressError = false,
}) => {
  const [addressValue, setAddressValue] = React.useState<string | undefined>();
  const [addressValueChanged, setAddressValueChanged] = React.useState(false);
  const prevAddressValue = React.useRef<string | undefined>(undefined);

  const {
    qasLookupAuthToken,
    originAddressesApiUrl,
    originAddressReverseLookupApiUrl,
    originBusinessTraceId,
  } = useEnergyAddressConfig();

  const reverseLookupUrl =
    originAddressReverseLookupApiUrl || ORIGIN_ADDRESS_REVERSE_LOOKUP_API_URL;

  const addressesUrl = originAddressesApiUrl || ORIGIN_ADDRESSES_API_URL;

  const { fetchAddresses, fetchAddressDetails } = qasLookup(qasLookupAuthToken, QasProfile.AUG);

  const { isNMIHardStop, isNMIServiceable, isNMIBelowThreshold } = useNMIResolution();
  const { nmiResolutionState, addresses } = useEnergyAddressLookup();

  const [endIcon, setEndIcon] = useAtom(addressLookupStatusAtom);

  const selectedEnergy = useAtomValue(selectedEnergyAtom);

  const setSelectedAddress = useSetAtom(selectedAddressAtom);
  const setUnconfirmedAddress = useSetAtom(unconfirmedAddressAtom);
  const setOriginAddresses = useSetAtom(originAddressesAtom);

  const [manualAddressEntryDialogOpen, setManualAddressEntryDialogOpen] = useAtom(
    addressManualDialogWithTabAtom
  );

  const manualAddressTab = useAtomValue(addressEntryManualDialogTabAtom);

  const [dedicatedNmiSearchModalOpen, setDedicatedNmiSearchModalOpen] = useAtom(
    addressEntryDedicatedNmiSearchDialogOpenAtom
  );

  const [nmiConfirmationDialogOpen, setNmiConfirmationDialogOpen] = useAtom(
    addressConfirmDialogOpenAtom
  );

  React.useEffect(() => {
    setEndIcon('');
    (async function fetchInitialAddress() {
      if (originAddressId) {
        const response = await asyncResult(reverseAddressLookup(originAddressId));

        if (response.error) {
          setEndIcon('error');
        }

        return response.data;
      }
    })();
  }, []);

  React.useEffect(() => {
    prevAddressValue.current !== addressValue
      ? setAddressValueChanged(true)
      : setAddressValueChanged(false);

    prevAddressValue.current = addressValue;
  }, [addressValue, selectedEnergy]);

  const getAddress = async (
    originAddressId: string
  ): Promise<ConnectionAddressObject | undefined> => {
    const response = await asyncResult(
      getAddressFromOriginAddressId(
        reverseLookupUrl,
        originAddressId,
        createTraceHeaders(originBusinessTraceId)
      )
    );

    if (response.error) {
      setEndIcon('error');
      return undefined;
    }

    const addressDetails = fromAddressToConnectionAddress(response.data);

    // The reverse-lookup endpoint is queried by originAddressId but doesn't echo
    // it back in the body, so the mapped address would otherwise lose it. Carry
    // the id we looked up by, so a restored address (e.g. on page reload) keeps
    // its originAddressId for downstream plan-availability calls.
    return {
      ...addressDetails,
      originAddressId: addressDetails.originAddressId ?? (originAddressId || undefined),
    };
  };

  const getAddresses = async (
    address: AddressManualEntryValues | Address,
    isManualSearch: boolean
  ): Promise<ConnectionAddressObject[]> => {
    const searchConfigParams = {
      resolveAddress: true,
      includeType2Discovery: true,
      isManualAddress: isManualSearch,
    };

    const response = await asyncResult(
      getOriginAddresses(
        addressesUrl,
        undefined,
        {
          ...(address as Address),
          ...searchConfigParams,
        },
        createTraceHeaders(originBusinessTraceId)
      )
    );

    if (response.error) {
      setEndIcon('error');
      return [];
    }

    const addresses = response.data.data as unknown as Address[];
    return addresses.map(fromAddressToConnectionAddress);
  };

  const getAddressesFromBatchId = async (batchId: string): Promise<ConnectionAddressObject[]> => {
    const response = await asyncResult(
      getOriginAddressesFromBatchId(
        addressesUrl,
        batchId,
        createTraceHeaders(originBusinessTraceId)
      )
    );

    if (response.error) {
      setEndIcon('error');
      return [];
    }

    return response.data.map(fromAddressToConnectionAddress);
  };

  const onConfirmNmiOrMirn = async (
    address: IOriginAddressMeter | ConnectionAddressObject | undefined,
    updateAddressInputValue = true
  ) => {
    setEndIcon('validating');
    setManualAddressEntryDialogOpen(false);
    setDedicatedNmiSearchModalOpen(false);

    handleOriginAddresses([]);

    if (!address) {
      return setEndIcon('error');
    }

    const addressDetails = await getAddress(address.originAddressId ?? '');

    if (!addressDetails) {
      return setEndIcon('error');
    }

    if (updateAddressInputValue) {
      setAddressValue(addressDetails.addressLabel);
    }

    handleSelectedAddress(addressDetails);
    setEndIcon('success');
  };

  const onSelect = async (result: AddressResult) => {
    setEndIcon('validating');
    setAddressValue(result.label);

    const address = await asyncResult(fetchAddressDetails(result.addressId));

    if (address.error) {
      return setEndIcon('error');
    }

    await handleOriginAddressesLookup(address.data);
  };

  const handleOriginAddressesLookup = async (
    address: AddressManualEntryValues | Address,
    isManualSearch = false
  ) => {
    const addresses = await getAddresses(address, isManualSearch);
    handleOriginAddresses(addresses);
    setEndIcon('success');

    // Clear any previously selected or unconfirmed address results
    setSelectedAddress(undefined);
    setUnconfirmedAddress(undefined);

    const addressDetails = await getAddress(addresses?.[0]?.originAddressId ?? '');

    if (!addressDetails) {
      return setEndIcon('error');
    }

    // Merge elecDetails from the addresses array with the connection address
    const connectionAddress = addElectricityDetailsToAddress(addressDetails, addresses);

    if (!isNMIHardStop(addresses) && !isNMIBelowThreshold(addresses, selectedEnergy)) {
      // Sets the selected address as it passes the required NMI checks
      handleSelectedAddress(connectionAddress);
    } else {
      handleUnconfirmedAddress(connectionAddress);
    }
  };

  const addElectricityDetailsToAddress = (
    address: ConnectionAddressObject,
    addresses: ConnectionAddressObject[]
  ): ConnectionAddressObject => {
    return {
      ...address,
      elecDetails: addresses?.[0]?.elecDetails || address.elecDetails,
    };
  };

  const onQueryChange = async (address: string) => {
    setEndIcon('');
    setAddressValue(address);
  };

  const onManualSearchSubmit = async (
    address: AddressManualEntryValues,
    _errors: AddressManualEntryError[]
  ) => {
    setAddressValue(constructDisplayLabel(address as Address));
    setEndIcon('validating');
    await handleOriginAddressesLookup(address, true);
  };

  const reverseAddressLookup = async (originAddressId: string) => {
    setEndIcon('validating');
    const address = await getAddress(originAddressId);

    if (!address) {
      return setEndIcon('error');
    }

    // If the address is already selected or does not have a batchId, we can treat it as a confirmed address
    if (address.isSelected || !address.batchId) {
      setAddressValue(address.qasAddress.label);
      handleSelectedAddress(address);
    } else {
      const addresses = await getAddressesFromBatchId(address.batchId);

      // Pick the first address from the list as the input value
      const firstAddress = addresses[0];
      setAddressValue(firstAddress.qasAddress.label);
      handleOriginAddresses(addresses);
      handleSelectedAddress(address);
    }

    setEndIcon('success');
  };

  const handleSelectedAddress = (address: ConnectionAddressObject) => {
    setSelectedAddress(address);
    onAddressSelected(address);
  };

  const handleUnconfirmedAddress = (address: ConnectionAddressObject) => {
    setUnconfirmedAddress(address);
    onAddressUnconfirmed?.(address);
  };

  const handleOriginAddresses = (addresses: ConnectionAddressObject[]) => {
    setOriginAddresses(addresses);
    onAddressesUpdated?.(addresses);
  };

  const showAddressLookupEmpty =
    (!addressValue || addressValue?.length === 0) && showEmptyAddressError;

  const getHelperText = (
    endIcon: AddressLookupProps['endIcon'],
    originAddressId: string | undefined,
    userLocationPostcode: string
  ): string | undefined => {
    if (showAddressLookupEmpty) {
      return 'Please enter your connection address...';
    }

    if (endIcon === 'error') {
      return 'Sorry - something went wrong. Please try again, or call 13 66 83.';
    }

    if (endIcon === '' && !originAddressId && showDefaultPricing) {
      return `Default pricing for postcode ${userLocationPostcode}`;
    }

    return undefined;
  };

  return (
    <>
      <AddressLookup
        id="address-lookup"
        data-id="address-lookup"
        endIcon={endIcon}
        error={endIcon === 'error' || showAddressLookupEmpty}
        disableClearable={true}
        fetchAddresses={fetchAddresses}
        forcePopupIcon={false}
        helperText={getHelperText(endIcon, originAddressId, userLocationPostcode)}
        label={label || 'Your connection address'}
        manualEntry={true}
        manualEntryProps={{
          id: 'address-lookup-manual-entry',
          'data-id': 'address-lookup-manual-entry',
          onManualEntryOpen: () => {
            setManualAddressEntryDialogOpen(true, 0);
          },
          onManualEntryClose: () => {
            setManualAddressEntryDialogOpen(false);
          },
          onManualNMIOpen: searchByNmi
            ? () => {
                setManualAddressEntryDialogOpen(true, 1);
              }
            : undefined,
          onManualMIRNOpen: searchByMirn
            ? () => {
                setManualAddressEntryDialogOpen(true, 2);
              }
            : undefined,
          onSubmit: onManualSearchSubmit,
        }}
        onQueryChange={onQueryChange}
        onSelect={onSelect}
        value={addressValue}
        AddressManualEntryComponent={(props) => (
          <AddressManualEntryDialog
            {...props}
            isOpen={manualAddressEntryDialogOpen}
            onClose={() => setManualAddressEntryDialogOpen(false)}
            defaultTab={manualAddressTab}
            searchByNmi={{
              enabled: searchByNmi,
              endpoint: reverseLookupUrl,
              onConfirm: onConfirmNmiOrMirn,
            }}
            searchByMirn={{
              enabled: searchByMirn,
              endpoint: reverseLookupUrl,
              onConfirm: onConfirmNmiOrMirn,
            }}
          />
        )}
      />

      {nmiResolutionState === NMIResolutionState.NmiHardStop && isNMIServiceable(addresses) && (
        <DedicatedNmiSearchModal
          autoOpen={() => {
            // Automatically open the dialog when selected energy is electricity & address has changed
            setDedicatedNmiSearchModalOpen(addressValueChanged && selectedEnergy.electricity);
          }}
          isOpen={dedicatedNmiSearchModalOpen}
          onClose={() => setDedicatedNmiSearchModalOpen(false)}
          searchByNmi={{
            endpoint: reverseLookupUrl,
            onConfirm: onConfirmNmiOrMirn,
          }}
        />
      )}

      <NMIResolution.ConfirmAddressDialog
        onConfirm={(address) => onConfirmNmiOrMirn(address, false)}
        onEnterAddressManually={() => {
          setManualAddressEntryDialogOpen(true, 0);
        }}
        onEnterNmiManually={() => {
          setManualAddressEntryDialogOpen(true, 1);
        }}
        open={nmiConfirmationDialogOpen}
        onClose={() => setNmiConfirmationDialogOpen(false)}
      />
      <NMIResolution.ConfirmAddressSuccess />
    </>
  );
};

const EnergyAddressHardStop: React.FC = () => {
  const setDedicatedNmiSearchModalOpen = useSetAtom(addressEntryDedicatedNmiSearchDialogOpenAtom);
  return (
    <NMIResolution.HardStopAlert triggerFindByNmi={() => setDedicatedNmiSearchModalOpen(true)} />
  );
};

const EnergyAddressCesErrorAlert: React.FC<{ customerType: CustomerType }> = ({ customerType }) => (
  <NMIResolution.CESErrorAlert customerType={customerType} />
);

const EnergyAddressBelowThreshold: React.FC = () => <NMIResolution.BelowThresholdAlert />;

export const EnergyAddressLookup = Object.assign(EnergyAddressLookupRoot, {
  HardStopAlert: EnergyAddressHardStop,
  BelowThresholdAlert: EnergyAddressBelowThreshold,
  CesErrorAlert: EnergyAddressCesErrorAlert,
});
